import axiosInstance from '../fetchConfig/axiosConfig';
import { getUserEmailById } from '../components/UserContext.js';
import logger from './logger';
import { TOWN_PORTAL_LINK_PERMISSIONS } from '../components/CaseReview/Offers/TownPortalFlow/townPortalLinkPermissions.js';

const STORAGE_PREFIX = 'townPortalFirstResponse:';
const LOG_PREFIX = '[TownPortalFirstResponse]';
const NOTIFY_CC_ADDRESSES = ['jrburns@aventine.ai'];

const linkSessionSummary = (linkSession) =>
  linkSession
    ? {
        muni_code: linkSession.muni_code,
        tax_year: linkSession.tax_year,
        court_date: linkSession.court_date,
        email: linkSession.email,
      }
    : null;

const firstResponseStorageKey = (linkSession) => {
  if (!linkSession?.muni_code || linkSession.tax_year == null) return null;
  const scopeKey = [
    linkSession.muni_code,
    linkSession.tax_year,
    linkSession.court_date ?? 'all',
  ].join('|');
  return STORAGE_PREFIX + scopeKey;
};

/** True if this link tab has not yet sent the first-response email for this scope. */
export const isFirstTownPortalResponseInSession = (linkSession) => {
  const storageKey = firstResponseStorageKey(linkSession);
  const alreadyNotified = storageKey ? sessionStorage.getItem(storageKey) : null;
  const isFirst = Boolean(storageKey && !alreadyNotified);
  if (!isFirst) {
    logger.log(`${LOG_PREFIX} isFirstTownPortalResponseInSession: false`, {
      storageKey,
      reason: !storageKey ? 'invalid linkSession (missing muni_code or tax_year)' : 'already notified this tab',
      linkSession: linkSessionSummary(linkSession),
    });
  }
  return isFirst;
};

/** @param {Array} offers - batch offers passed to updateOffersBatch */
export const batchHasMeaningfulTownOffer = (offers) => {
  const list = offers || [];
  const result = list.some((o) => {
    if (o.offeredBy !== 'Town') return false;
    const val = o.offerValue;
    if (val == null || val === '') return false;
    const n = Number(val);
    return !Number.isNaN(n) && n > 0;
  });
  if (!result) {
    logger.log(`${LOG_PREFIX} batchHasMeaningfulTownOffer: false`, {
      offerCount: list.length,
      townOffers: list
        .filter((o) => o.offeredBy === 'Town')
        .map((o) => ({ parcelId: o.parcelId, offerValue: o.offerValue })),
    });
  }
  return result;
};

const buildSubject = (linkSession) => {
  const { muni_code, tax_year, court_date } = linkSession;
  const courtPart =
    court_date != null && court_date !== '' ? ` ${court_date}` : '';
  return `Town portal response — ${muni_code}${courtPart} (${tax_year})`;
};

const ACTION_LABELS = {
  accept_offer: 'Accept Offer',
  town_offer_update: 'Counter Offer',
};

const formatResponseLabel = (action) =>
  ACTION_LABELS[action] ||
  String(action || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildMessage = ({ linkSession, action, parcelId }) => {
  const hasCourtDate =
    linkSession.court_date != null && linkSession.court_date !== '';
  const caseInfo = [
    `Municipality: ${linkSession.muni_code}`,
    `Tax Year: ${linkSession.tax_year}`,
    ...(hasCourtDate
      ? [`Court Date Scope: ${linkSession.court_date}`]
      : []),
    `Parcel ID: ${parcelId || '(unknown)'}`,
    `Response Received: ${formatResponseLabel(action)}`,
  ];

  return [
    'A town user has submitted the first response in their session through the Town Portal.',
    '',
    'This initial response often indicates that additional responses may follow shortly. Please review the corresponding municipality and tax year within the next day to determine whether the town has submitted any further responses.',
    '',
    'Case Information',
    '',
    ...caseInfo,
    '',
    `Town User: ${linkSession.email || '(unknown)'}`,
    '',
    'Portal Access Settings',
    ...TOWN_PORTAL_LINK_PERMISSIONS.map((p) => {
      const label = p.dialogLabel || p.apiKey;
      return `${label}: ${linkSession?.[p.userKey] ? 'Yes' : 'No'}`;
    }),
    '',
  ].join('\n');
};

/**
 * Once per link session (tab), email when the town first counters or accepts.
 * @param {{ muni_code, tax_year, court_date?, email?, shareWorkups?, ShowGridComps? }} linkSession
 * @param {number|string} [negotiatorId] - Negotiations.Negotiator (Aventine issuer from portal link)
 */
export const maybeNotifyTownPortalFirstResponse = ({
  linkSession,
  action,
  parcelId,
  negotiatorId,
}) => {
  logger.log(`${LOG_PREFIX} maybeNotify called`, {
    action,
    parcelId,
    negotiatorId,
    linkSession: linkSessionSummary(linkSession),
  });

  const storageKey = firstResponseStorageKey(linkSession);
  if (!storageKey) {
    logger.warn(`${LOG_PREFIX} skip: invalid linkSession`, {
      linkSession: linkSessionSummary(linkSession),
    });
    return;
  }
  if (sessionStorage.getItem(storageKey)) {
    logger.log(`${LOG_PREFIX} skip: already notified this tab`, { storageKey, action });
    return;
  }
  const toAddress =
    negotiatorId != null && negotiatorId !== ''
      ? getUserEmailById(negotiatorId) || null
      : null;
  if (!toAddress) {
    logger.warn(`${LOG_PREFIX} skip: no negotiator email`, {
      storageKey,
      negotiatorId,
      action,
    });
    return;
  }

  sessionStorage.setItem(storageKey, '1');
  logger.log(`${LOG_PREFIX} sending email`, {
    storageKey,
    action,
    parcelId,
    negotiatorId,
    toAddress,
  });

  const message = buildMessage({ linkSession, action, parcelId });

  void axiosInstance
    .post('/send_internal_email', {
      toAddress: [toAddress],
      ccAddress: NOTIFY_CC_ADDRESSES,
      subject: buildSubject(linkSession),
      message,
    })
    .then(() =>
      logger.log(`${LOG_PREFIX} email sent`, { storageKey, action, parcelId }),
    )
    .catch((err) =>
      logger.error(`${LOG_PREFIX} email failed`, {
        storageKey,
        action,
        parcelId,
        err: err?.message ?? err,
        response: err?.response?.data,
      }),
    );
};

/** Build link-session payload from link-authenticated user (town portal). */
export const linkSessionFromUser = (user) => {
  if (!user?.isLinkAuthenticated) {
    logger.log(`${LOG_PREFIX} linkSessionFromUser: null (not link-authenticated)`);
    return null;
  }
  return {
    muni_code: user.muni_code,
    tax_year: user.tax_year,
    court_date: user.court_date ?? null,
    email: user.email,
    ...Object.fromEntries(
      TOWN_PORTAL_LINK_PERMISSIONS.map((p) => [p.userKey, Boolean(user?.[p.userKey])]),
    ),
  };
};
